import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, InputAdornment, Chip, CircularProgress, Alert, IconButton } from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    ArrowBack as ArrowBackIcon,
    AccessTime as AccessTimeIcon,
    AspectRatio as AspectRatioIcon,
    HighQuality as HighQualityIcon,
    Brush as BrushIcon,
    Category as CategoryIcon,
    SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { SCUIButton } from '@/atomic/atoms';
import AIProcessLogQuery, { AIProcessLogItem } from '../../../../services/query/AIProcessLogQuery';
import { getRelativeTime, getModelTypeLabel } from './AIGeneration.helper';
import HistoryDetailModal from './HistoryDetailModal';
import { PageUrl } from '../../../../types/enums/pageUrl-enums';
import './styles/AIGenerationHistory.scss';
import { SCUIContent } from '@/atomic/organisms';

const AIGenerationHistory = () => {
    const { t } = useTranslation();
    const history = useHistory();

    const [historyPages, setHistoryPages] = useState<AIProcessLogItem[][]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchBarcode, setSearchBarcode] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState<AIProcessLogItem | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const pageSize = 12;

    useEffect(() => {
        fetchHistory(1);
    }, []);

    const fetchHistory = async (page: number, append: boolean = false, barcode?: string) => {
        try {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setIsInitialLoading(true);
            }
            setError(null);

            const response = await AIProcessLogQuery.GetUserHistory({
                pageNumber: page,
                pageSize,
                barcode: barcode,
            });

            const items = response.items || response.data || [];
            const totalPagesCount = response.totalPages || Math.ceil((response.totalCount || 0) / pageSize);

            if (append) {
                if (items.length > 0) {
                    setHistoryPages((prev) => [...prev, items]);
                }
            } else {
                setHistoryPages(items.length > 0 ? [items] : []);
            }

            setPageNumber(page);
            setHasMore(page < totalPagesCount);
        } catch (err: any) {
            console.error('Error fetching history:', err);
            setError(err.message || 'Failed to load history');
            if (!append) {
                setHistoryPages([]);
            }
        } finally {
            if (append) {
                setIsLoadingMore(false);
            } else {
                setIsInitialLoading(false);
            }
        }
    };

    // Barkod ile API'den arama
    const handleSearch = () => {
        fetchHistory(1, false, searchBarcode.trim() || undefined);
    };

    const handleClearSearch = () => {
        setSearchBarcode('');
        fetchHistory(1);
    };

    const handleLoadMore = () => {
        if (!isLoadingMore && hasMore) {
            fetchHistory(pageNumber + 1, true, searchBarcode.trim() || undefined);
        }
    };

    const handleViewDetails = (item: AIProcessLogItem) => {
        setSelectedHistoryItem(item);
        setIsDetailModalOpen(true);
    };

    const handleRegenerate = (item: AIProcessLogItem) => {
        // Save only the ID to localStorage - full data with base64 images can exceed localStorage quota
        localStorage.setItem('pendingHistoryItemId', String(item.id));
        history.push(PageUrl.studioRoutes.aiGenerationHanger.url);
    };

    const handleBack = () => {
        history.push(PageUrl.studioRoutes.aiGenerationHanger.url);
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'success':
                return 'success';
            case 'failed':
            case 'error':
                return 'error';
            case 'processing':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Render helpers
    const renderLoadingState = () => (
        <Box className="loading-container">
            <CircularProgress size={48} />
            <Typography color="textSecondary">{t('mannequin.aiGeneration.hangerFlow.history.loading')}</Typography>
        </Box>
    );

    const renderEmptyState = () => (
        <Box className="empty-state">
            <Typography variant="h6" gutterBottom>
                {searchBarcode
                    ? t('mannequin.aiGeneration.hangerFlow.history.noResults')
                    : t('mannequin.aiGeneration.hangerFlow.history.empty')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {searchBarcode
                    ? `"${searchBarcode}" ${t('mannequin.aiGeneration.hangerFlow.history.noResultsFor')}`
                    : t('mannequin.aiGeneration.hangerFlow.history.emptyDescription')}
            </Typography>
            {searchBarcode && (
                <SCUIButton
                    text={t('mannequin.aiGeneration.hangerFlow.history.clearSearch')}
                    size="s"
                    sizeType="default"
                    type="secondary"
                    onClick={handleClearSearch}
                />
            )}
        </Box>
    );

    const isListEmpty = historyPages.length === 0 || historyPages[0]?.length === 0;

    const renderHistoryCard = (item: AIProcessLogItem) => (
        <Box className="history-card" key={item.id}>
            <Box className="card-header">
                <Typography className="barcode">{item.productBarcode || '-'}</Typography>
                <Chip
                    label={item.status}
                    size="small"
                    color={getStatusColor(item.status)}
                    className="status-chip"
                />
            </Box>

            <Typography className="prompt" title={item.prompt}>
                {item.prompt || '-'}
            </Typography>

            <Box className="config-tags" sx={{ mb: 0.5 }}>
                {(item.generationTypeName || item.generationTypeCode) && (
                    <Chip
                        icon={<CategoryIcon sx={{ fontSize: '14px !important' }} />}
                        label={item.generationTypeName || item.generationTypeCode}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                )}
                {item.isInpainting && (
                    <Chip
                        icon={<BrushIcon sx={{ fontSize: '14px !important' }} />}
                        label={t('mannequin.aiGeneration.hangerFlow.history.inpainting')}
                        size="small"
                        color="secondary"
                        variant="filled"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                )}
                {item.modelType !== undefined && (
                    <Chip
                        icon={<SmartToyIcon sx={{ fontSize: '14px !important' }} />}
                        label={getModelTypeLabel(item.modelType, true)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                )}
            </Box>

            <Box className="config-tags">
                <Box className="tag">
                    <HighQualityIcon />
                    <span>{item.resolution || '-'}</span>
                </Box>
                <Box className="tag">
                    <AspectRatioIcon />
                    <span>{item.aspectRatio || '-'}</span>
                </Box>
                <Box className="tag">
                    <AccessTimeIcon />
                    <span>{getRelativeTime(item.createdDate)}</span>
                </Box>
            </Box>

            <Box className="card-actions">
                <SCUIButton
                    text={t('mannequin.aiGeneration.hangerFlow.history.viewDetails')}
                    size="s"
                    sizeType="default"
                    type="primary"
                    onClick={() => handleViewDetails(item)}
                    showStartIcon
                    startIcon={<VisibilityIcon />}
                    fullwidth
                />
            </Box>
        </Box>
    );

    return (
        <SCUIContent>
            <Box className="ai-generation-history">
                {/* Header */}
                <Box className="history-header">
                    <Box className="header-left">
                        <IconButton onClick={handleBack} className="back-btn">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" className="history-title">
                            {t('mannequin.aiGeneration.hangerFlow.history.pageTitle')}
                        </Typography>
                    </Box>
                    <Box className="header-right">
                        <TextField
                            size="small"
                            placeholder={t('mannequin.aiGeneration.hangerFlow.history.filters.search')}
                            value={searchBarcode}
                            onChange={(e) => setSearchBarcode(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            className="search-field"
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ width: 300 }}
                        />
                        <IconButton
                            onClick={handleSearch}
                            className="search-btn"
                            title={t('mannequin.aiGeneration.hangerFlow.history.filters.searchButton')}
                        >
                            <SearchIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleClearSearch}
                            className="refresh-btn"
                            title={t('mannequin.aiGeneration.hangerFlow.history.clearSearch')}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* History Grid */}
                {isInitialLoading && renderLoadingState()}

                {!isInitialLoading && isListEmpty && renderEmptyState()}

                {!isInitialLoading && !isListEmpty && (
                    <>
                        {historyPages.map((pageItems, pageIndex) => (
                            <Box
                                className={`history-grid${pageIndex > 0 ? ' history-grid--appended' : ''}`}
                                key={`history-page-${pageIndex}`}
                            >
                                {pageItems.map((item) => renderHistoryCard(item))}
                            </Box>
                        ))}

                        {hasMore && !searchBarcode && (
                            <Box className="load-more-container">
                                <SCUIButton
                                    text={t('mannequin.aiGeneration.hangerFlow.history.loadMore')}
                                    size="m"
                                    sizeType="default"
                                    type="secondary"
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                />
                            </Box>
                        )}

                        {isLoadingMore && (
                            <Box className="load-more-loading">
                                <CircularProgress size={32} />
                                <Typography color="textSecondary">
                                    {t('mannequin.aiGeneration.hangerFlow.history.loading')}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                {/* Detail Modal */}
                <HistoryDetailModal
                    open={isDetailModalOpen}
                    onClose={() => setIsDetailModalOpen(false)}
                    historyItem={selectedHistoryItem}
                    onRegenerate={handleRegenerate}
                />
            </Box>
        </SCUIContent>
    );
};

export default AIGenerationHistory;
